import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { ArrowLeft, Mail, MessageCircle } from 'lucide-react';
import { ApiError } from '@/shared/api/client';
import { MaskedInput } from '@/shared/components/masked-input';
import { maskPhone } from '@/shared/lib/input-masks';
import { onlyDigits } from '@/shared/lib/format';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { participantApi, type OtpChannel, type ParticipantMe } from '../api/participant-api';
import { isValidIdentity, maskIdentity, type LoginIdentity } from '../model/form-plan';
import { openWhatsapp } from '../model/open-whatsapp';

const RESEND_SECONDS = 60;
const PENDING_TTL_MS = 10 * 60 * 1000;

interface PendingWhatsapp {
  uniqueId: string;
  phone: string;
  destination: string;
  whatsappUrl: string;
  at: number;
}

const pendingKey = (idPassbook: string) => `participant-otp:${idPassbook}`;

/**
 * O WhatsApp abre na mesma aba (aba nova ficava em about:blank na volta do
 * app). O passo do código fica guardado na aba para sobreviver à navegação.
 */
function readPending(idPassbook: string): PendingWhatsapp | null {
  try {
    const raw = window.sessionStorage.getItem(pendingKey(idPassbook));
    if (!raw) return null;
    const pending = JSON.parse(raw) as PendingWhatsapp;
    if (!pending.whatsappUrl || Date.now() - pending.at > PENDING_TTL_MS) {
      window.sessionStorage.removeItem(pendingKey(idPassbook));
      return null;
    }
    return pending;
  } catch {
    return null;
  }
}

function writePending(idPassbook: string, pending: PendingWhatsapp | null) {
  try {
    if (pending) window.sessionStorage.setItem(pendingKey(idPassbook), JSON.stringify(pending));
    else window.sessionStorage.removeItem(pendingKey(idPassbook));
  } catch {
    // sessionStorage bloqueado (aba anônima antiga): segue sem restaurar.
  }
}

interface ParticipantLoginProps {
  idPassbook: string;
  /** CPF ou CNPJ, conforme o cadastro da loja. */
  identity: LoginIdentity;
  identityLabel: string;
  accentColor?: string;
  foregroundColor?: string;
  onSignedIn: (me: ParticipantMe) => void;
  /** CPF sem cadastro nesta loja: leva para a bolinha Cadastro. */
  onGoToRegister: () => void;
}

type Step =
  | { kind: 'identify' }
  | { kind: 'code'; channel: OtpChannel; destination: string; whatsappUrl?: string };

/**
 * Entrar com CPF + celular do cadastro → código de 5 números → sessão. No
 * WhatsApp a pessoa manda a mensagem pronta ao número da Fixe e o código chega
 * na resposta (mesmo fluxo do app); no e-mail o código é enviado.
 */
export function ParticipantLogin({
  idPassbook,
  identity,
  identityLabel,
  accentColor,
  foregroundColor,
  onSignedIn,
  onGoToRegister,
}: ParticipantLoginProps) {
  const [pending] = useState(() => readPending(idPassbook));
  const [step, setStep] = useState<Step>(() =>
    pending
      ? { kind: 'code', channel: 'whatsapp', destination: pending.destination, whatsappUrl: pending.whatsappUrl }
      : { kind: 'identify' },
  );
  const [uniqueId, setUniqueId] = useState(pending?.uniqueId ?? '');
  const [phone, setPhone] = useState(pending?.phone ?? '');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; notMember?: boolean } | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    if (step.kind === 'code') codeRef.current?.focus();
  }, [step.kind]);

  const accentButton: CSSProperties | undefined = accentColor
    ? { backgroundColor: accentColor, color: foregroundColor || '#fff' }
    : undefined;

  const handleError = (err: unknown) => {
    if (err instanceof ApiError) {
      const notMember = (err.data as { code?: string } | undefined)?.code === 'not_member';
      setError({ message: err.message, notMember });
    } else {
      setError({ message: 'Sem conexão. Confira sua internet e tente de novo.' });
    }
  };

  const sendCode = async (channel: OtpChannel) => {
    setError(null);
    if (!isValidIdentity(identity, uniqueId)) return setError({ message: `Informe um ${identityLabel} válido.` });
    if (onlyDigits(phone).length !== 11) return setError({ message: 'Informe o celular com DDD.' });
    setBusy(true);
    try {
      const sent = await participantApi.sendCode(idPassbook, { cpf: onlyDigits(uniqueId), phone: onlyDigits(phone), channel });
      setCode('');
      setStep({ kind: 'code', channel: sent.channel, destination: sent.destination, whatsappUrl: sent.whatsappUrl });
      setResendIn(RESEND_SECONDS);
      if (sent.whatsappUrl) {
        writePending(idPassbook, {
          uniqueId,
          phone,
          destination: sent.destination,
          whatsappUrl: sent.whatsappUrl,
          at: Date.now(),
        });
        openWhatsapp(sent.whatsappUrl);
      } else {
        writePending(idPassbook, null);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (value: string) => {
    if (value.length !== 5 || busy) return;
    setError(null);
    setBusy(true);
    try {
      const me = await participantApi.verifyCode(idPassbook, { cpf: onlyDigits(uniqueId), token: value });
      writePending(idPassbook, null);
      onSignedIn(me);
    } catch (err) {
      handleError(err);
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const onIdentify = (e: FormEvent) => {
    e.preventDefault();
    void sendCode('whatsapp');
  };

  const onCode = (e: FormEvent) => {
    e.preventDefault();
    void verify(code);
  };

  const errorBox = error ? (
    <div role="alert" className="space-y-3 rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
      <p>{error.message}</p>
      {error.notMember ? (
        <Button type="button" size="sm" variant="outline" onClick={onGoToRegister}>
          Fazer meu cadastro
        </Button>
      ) : null}
    </div>
  ) : null;

  if (step.kind === 'code') {
    const viaWhatsapp = step.channel === 'whatsapp' && Boolean(step.whatsappUrl);
    return (
      <Card className="mx-auto w-full max-w-xl">
        <form onSubmit={onCode}>
          <CardHeader>
            <CardTitle>Digite o código</CardTitle>
            <CardDescription>
              {viaWhatsapp ? (
                <>
                  Envie a mensagem que abrimos no WhatsApp da Fixe, pelo celular{' '}
                  <b className="text-foreground">{step.destination}</b>. Você vai receber o código de 5 números lá.
                </>
              ) : (
                <>
                  Enviamos um código de 5 números {step.channel === 'email' ? 'para o e-mail' : 'no WhatsApp'}{' '}
                  <b className="text-foreground">{step.destination}</b>.
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="participant-code">Código</Label>
              <Input
                ref={codeRef}
                id="participant-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={5}
                placeholder="00000"
                className="h-14 text-center text-2xl tracking-[0.5em]"
                value={code}
                onChange={(e) => {
                  const value = onlyDigits(e.target.value).slice(0, 5);
                  setCode(value);
                  if (value.length === 5) void verify(value);
                }}
              />
            </div>
            {errorBox}
            <Button
              type="submit"
              size="lg"
              className={cn('w-full', accentButton && 'hover:opacity-90')}
              style={accentButton}
              loading={busy}
              disabled={code.length !== 5}
            >
              Entrar
            </Button>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setError(null);
                  writePending(idPassbook, null);
                  setStep({ kind: 'identify' });
                }}
              >
                <ArrowLeft className="size-4" />
                Trocar {identityLabel} ou celular
              </Button>
              {viaWhatsapp ? (
                <Button asChild variant="ghost" size="sm">
                  <a href={step.whatsappUrl} rel="noopener noreferrer">
                    <MessageCircle className="size-4" />
                    Abrir o WhatsApp de novo
                  </a>
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={resendIn > 0 || busy}
                  onClick={() => void sendCode(step.channel)}
                >
                  {resendIn > 0 ? `Reenviar em ${resendIn}s` : 'Reenviar código'}
                </Button>
              )}
            </div>
            {step.channel === 'whatsapp' ? (
              <button
                type="button"
                className="w-full text-center text-xs text-muted-foreground underline-offset-4 hover:underline disabled:opacity-50"
                disabled={resendIn > 0 || busy}
                onClick={() => void sendCode('email')}
              >
                Não chegou? Receber pelo e-mail do cadastro
              </button>
            ) : null}
          </CardContent>
        </form>
      </Card>
    );
  }

  return (
    <Card className="mx-auto w-full max-w-xl">
      <form onSubmit={onIdentify}>
        <CardHeader>
          <CardTitle>Já sou cliente</CardTitle>
          <CardDescription>
            Entre com o {identityLabel} e o celular do seu cadastro para ver seus pontos, cupons e atualizar seus
            dados.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="participant-identity">{identityLabel}</Label>
            <MaskedInput
              id="participant-identity"
              inputMode="numeric"
              autoComplete="off"
              placeholder={identity === 'cnpj' ? '00.000.000/0000-00' : '000.000.000-00'}
              value={uniqueId}
              onValueChange={setUniqueId}
              mask={(v) => maskIdentity(identity, v)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="participant-phone">Celular</Label>
            <MaskedInput
              id="participant-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              placeholder="(00) 00000-0000"
              value={phone}
              onValueChange={setPhone}
              mask={maskPhone}
            />
          </div>
          {errorBox}
          <Button
            type="submit"
            size="lg"
            className={cn('w-full', accentButton && 'hover:opacity-90')}
            style={accentButton}
            loading={busy}
          >
            <MessageCircle className="size-4" />
            Receber código no WhatsApp
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            disabled={busy}
            onClick={() => void sendCode('email')}
          >
            <Mail className="size-4" />
            Prefiro receber por e-mail
          </Button>
        </CardContent>
      </form>
    </Card>
  );
}
